import type { TranscriptTurnPayload } from '@Application/Beneficiaries/Transcripts/Actions/transcriptTurnsFetched';

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `speakerId` is read from `payload.speaker?.id ?? null`: the wire relation is an object or `null`,
 * never a bare id (§8.1). `originalText` and the two timestamps are nullable states the payload can
 * legitimately carry — `null` never means "empty", it is read as-is, never coerced.
 */
class TranscriptTurnView {
	readonly id: number;
	readonly position: number;
	readonly text: string;
	readonly originalText: string | null;
	readonly startSecond: number | null;
	readonly endSecond: number | null;
	readonly speakerId: number | null;

	constructor(payload: TranscriptTurnPayload) {
		this.id = payload.id;
		this.position = payload.position;
		this.text = payload.text;
		this.originalText = payload.originalText;
		this.startSecond = payload.startSecond;
		this.endSecond = payload.endSecond;
		this.speakerId = payload.speaker?.id ?? null;

		Object.freeze(this);
	}

	get isRevised(): boolean {
		return this.originalText !== null;
	}

	get hasTimestamp(): boolean {
		return this.startSecond !== null;
	}

	get timecodeLabel(): string | null {
		if (this.startSecond === null) {
			return null;
		}

		const totalSeconds = Math.floor(this.startSecond);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;

		return `${pad(minutes)}:${pad(seconds)}`;
	}
}

export default TranscriptTurnView;
